import * as React from "react";
import "./styles/main.scss";
import { Box } from "@mui/material";
import { Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { useKeycloak } from "@react-keycloak/web";
import Loading from "./components/ui/Loading";
import { AnimatePresence } from "framer-motion";
import { ColorModeProvider } from "./contexts/ColorModeContext";
import NotificationProvider from "./components/ui/Notification/NotificationProvider";
import { showNotification } from "./store/slices/notificationSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "./store/store";
import { Notification, UserResponse } from "./_app/types";
import { HubError } from "./class/Error";
import {
  newNotification,
  setTelegramUser,
  setUser,
  switchAccount,
  updateToken,
} from "./store/slices/userSlice";
import { useWebApp } from "@vkruglikov/react-telegram-web-app";
import { decodeUserHash, parseTelegramUserData } from "./utils/utils";
import { loadTickers, loginWithPasswordGrant } from "./utils/async";
import CreationPage from "./components/pages/CreationPage";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { setTickers } from "./store/slices/sharedData";
import FuturesBotListPage from "./_pages/FuturesBotListPage/FuturesBotListPage";
import AutoStrategyListPage from "./_pages/AutoStrategyListPage/AutoStrategyListPage";
import { BotCategoriesPage } from "./_pages/BotCategoriesPage/BotCategoriesPage";
import TickerChoosePage from "./_pages/TickerChoosePage/TickerChoosePage";
import NavBar from "./_modules/NavBar/NavBar";
import ConnectAccountPage from "./_pages/ConnectAccountPage/ConnectAccountPage";
import AccountCenterPage from "./_pages/AccountCenterPage/AccountCenterPage";
import { useApi } from "./_hooks/use-api";
import ProfilePage from "./_pages/ProfilePage/ProfilePage";
import AppearancePage from "./_pages/AppearancePage/AppearancePage";
import LanguagePage from "./_pages/LanguagePage/LanguagePage";
import NotificationSettingsPage from "./_pages/NotificationSettingsPage/NotificationSettingsPage";
import AboutPage from "./_pages/AboutPage/AboutPage";
import ChooseMarketsPage from "./_pages/ChooseMarketsPage/ChooseMarketsPage";
import AccountSettingsPage from "./_pages/AccountSettingsPage/AccountSettingsPage";
import AssetsPage from "./_pages/AssetsPage/AssetsPage";
import LeaderboardPage from "./_pages/LeaderboardPage/LeaderboardPage";
import { useAppNavigate } from "./_hooks/use-app-navigate";
import SubscriptionPage from "./_pages/SubscriptionPage/SubscriptionPage";
import FutureBotDetailsPage from "./_pages/FuturesBotDetailsPage/FutureBotDetailsPage";
import { APP_ID, NO_NAV_BAR_PAGES, route, TG_DATA } from "./_app/consts";
import AutoStrategyDetailsPage from "./_pages/AutoStrategyDetailPage/AutoStrategyDetailsPage";
import useLocalStorage from "./_hooks/useLocalStorage";
import OnboardingPage from "./_pages/OnboardingPage/OnboardingPage";

export default function App() {
  const dispatch = useDispatch();
  const { keycloak, initialized } = useKeycloak();
  const user = useSelector((state: RootState) => state.user);
  const WebApp = useWebApp();
  const api = useApi();

  const navigate = useAppNavigate();
  const location = useLocation();

  const [hasCompleteOnboarding] = useLocalStorage(`hasCompleteOnboarding${APP_ID}`);
  const [notifications, setNotifications] = React.useState<Notification[]>([]);

  const handleNotifications = (items: Notification[]) => {
    setNotifications((prev) => [...prev, ...items]);
  };

  useEffect(() => {
    sessionStorage.setItem("lastPath", location.pathname + location.search);
  }, [location]);

  async function getProfile(token: string, dispatch: AppDispatch) {
    try {
      const { profileId }: { profileId: string } = jwtDecode(token);
      const response: UserResponse = await api.getUserProfile(profileId);

      if (response.settings) {
        const activeAccount = response.settings.find((item) => item.name === "ACTIVE_ACCOUNT_ID");
        if (activeAccount && response.accounts) {
          const accountToSwitch = response.accounts.find((item) => item.id === activeAccount.value);
          accountToSwitch
            ? dispatch(switchAccount(accountToSwitch))
            : dispatch(
                switchAccount({
                  id: "",
                  name: "",
                  market: "",
                  marginType: "",
                  leverage: 0,
                  linkDateTime: null,
                })
              );
        }
      }
      dispatch(setUser(response));

      if (hasCompleteOnboarding) {
        const lastPath = sessionStorage.getItem("lastPath");
        if (
          lastPath !== "/" &&
          lastPath !== "/creation/bot" &&
          lastPath !== "/creation/strategy" &&
          lastPath !== null
        ) {
          navigate.lastPath(lastPath);
        } else {
          navigate.bots();
        }
      } else {
        navigate.onboarding();
      }
    } catch (error: any) {
      if (error instanceof HubError) {
        if (error.errorCode === "AMNG_02") {
          try {
            const { profileId }: { profileId: string } = jwtDecode(token);
            await api.createUserProfile();
            try {
              const response: UserResponse = await api.getUserProfile(profileId);
              if (response.settings) {
                const activeAccount = response.settings.find(
                  (item) => item.name === "ACTIVE_ACCOUNT_ID"
                );
                if (activeAccount && response.accounts) {
                  const accountToSwitch = response.accounts.find(
                    (item) => item.id === activeAccount.value
                  );
                  accountToSwitch && dispatch(switchAccount(accountToSwitch));
                }
              }
              dispatch(setUser(response));
              navigate.bots();
            } catch (error: any) {
              error instanceof HubError
                ? dispatch(
                    showNotification({
                      message: `${error.message} ${error.detailedMessage}`,
                      severity: "error",
                    })
                  )
                : dispatch(
                    showNotification({
                      message: error.message,
                      severity: "error",
                    })
                  );
            }
          } catch (error: any) {
            error instanceof HubError
              ? dispatch(
                  showNotification({
                    message: `${error.message} ${error.detailedMessage}`,
                    severity: "error",
                  })
                )
              : dispatch(
                  showNotification({
                    message: error.message,
                    severity: "error",
                  })
                );
          }
        } else {
          dispatch(
            showNotification({
              message: `${error.message} ${error.detailedMessage}`,
              severity: "error",
            })
          );
        }
      } else {
        console.log(error.message, "error");
      }
    }
  }

  useEffect(() => {
    if (WebApp) {
      !WebApp.isExpanded && WebApp.expand();
      WebApp.isVerticalSwipesEnabled && WebApp.disableVerticalSwipes();
      WebApp.lockOrientation();
      !WebApp.isClosingConfirmationEnabled && WebApp.enableClosingConfirmation();
      const initData = process.env.NODE_ENV === "development" ? TG_DATA : WebApp.initData;

      const userFromTelegram = !!initData;
      if (userFromTelegram || initData !== "") {
        loginWithPasswordGrant(decodeURIComponent(initData), decodeUserHash(initData), keycloak)
          .then((response) => {
            getProfile(response.access_token, dispatch);
            dispatch(updateToken(response.access_token as string));
          })
          .catch((error) => {
            console.error("Login failed:", error);
          });
        dispatch(setTelegramUser(parseTelegramUserData(initData)));
      } else {
        dispatch(
          showNotification({
            message: `Use telegram mini app`,
            severity: "info",
          })
        );
        // const token = localStorage.getItem("token" + APP_ID);
        // if (token && isTokenValid(token)) {
        // 	getProfile(token);
        // } else {
        // 	if (keycloak) {
        // 		if (keycloak.authenticated && keycloak.token) {
        // 			localStorage.setItem("token" + APP_ID, keycloak.token);
        // 			localStorage.setItem("refreshToken" + APP_ID, keycloak.refreshToken as string);
        // 			setToken(keycloak.token as string);
        // 			getProfile(keycloak.token as string);
        // 		}
        //
        // 		keycloak.onAuthSuccess = () => {
        // 			const token = keycloak.token;
        // 			console.log(token);
        // 			if (token) {
        // 				localStorage.setItem("token" + APP_ID, token);
        // 				setToken(token);
        // 				getProfile(token);
        // 			}
        // 			dispatch(
        // 				showNotification({
        // 					message: `Welcome to Smart Tools`,
        // 					severity: 'success',
        // 				})
        // 			);
        // 		};
        //
        // 		keycloak.onAuthLogout = () => localStorage.removeItem("token" + APP_ID);
        // 		keycloak.onAuthError = () => console.error("Authentication failed");
        // 	}
        // }
      }
    }

    return () => {
      // при размонтировании приложения разлогируем пользователя
      if (keycloak) {
        keycloak.logout();
      }
    };
  }, []);

  useEffect(() => {
    if (WebApp) {
      if (
        WebApp.platform === "web" ||
        WebApp.platform === "tdesktop" ||
        WebApp.platform === "macos"
      ) {
        document.documentElement.style.overflowY = "auto";
      } else if (WebApp.platform === "android" || WebApp.platform === "ios") {
        document.documentElement.style.overflowY = "hidden";
      }
    }
  }, []);

  const refreshToken = () => {
    if (keycloak) {
      const storedRefreshToken = localStorage.getItem("refreshToken" + APP_ID);
      if (!keycloak.refreshToken && storedRefreshToken) {
        keycloak.refreshToken = storedRefreshToken;
      }

      keycloak
        .updateToken(180)
        .then((refreshed) => {
          if (refreshed) {
            localStorage.setItem("token" + APP_ID, keycloak.token as string);
            localStorage.setItem("refreshToken" + APP_ID, keycloak.refreshToken as string);
            dispatch(updateToken(keycloak.token as string));
            // console.log('updated token', jwtDecode<any>( keycloak.token as string).exp);
            // setToken(keycloak.token as string);
            return true;
          }
        })
        .catch(() => {
          console.log("Failed to refresh the token, or the session has expired");
        });
    }
    return false;
  };

  useEffect(() => {
    const interval = setInterval(refreshToken, 180000);
    return () => clearInterval(interval);
  }, [keycloak]);

  useEffect(() => {
    if (user.activeAccount.id === "" || !user.token) return;

    const newController = new AbortController();
    const { signal } = newController;

    const fetchData = async () => {
      try {
        await fetchEventSource(
          `${process.env.REACT_APP_API_NOTIFICATION}/api/v1/account-notifications?accountId=${user.activeAccount.id}`,
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
            onmessage(event: any) {
              const parsedData = JSON.parse(event.data);
              dispatch(newNotification());
              dispatch(
                showNotification({
                  message: `${parsedData.message}`,
                  severity: "success",
                })
              );
              // console.log(parsedData);
              setNotifications((prevMessages) => [...prevMessages, parsedData]);
            },
            onerror(error) {
              //FIXME disabled throw error
              // throw new Error(error); // test disabled throw
            },
            signal,
          }
        );
      } catch (error: any) {
        console.error(error);
      }
    };

    fetchData();
    return () => newController.abort();
  }, [user.activeAccount.id, user.token]);

  useEffect(() => {
    setNotifications([]);
  }, [user.activeAccount.id]);

  const fetchTickers = async () => {
    const tickers = await loadTickers(user.activeAccount.market, api);
    dispatch(setTickers(tickers));
  };

  useEffect(() => {
    if (!user.profile.profileId) return;
    if (user.activeAccount.market === "") return;
    if (!user.token) return;

    fetchTickers();
  }, [user.activeAccount.market, user.profile.profileId]);

  if (!initialized && !localStorage.getItem("token" + APP_ID)) {
    return <Loading />;
  }

  return (
    <ColorModeProvider>
      {user.profile.profileId && (
        <Box className="main-layout">
          <Box className="main-content">
            <AnimatePresence mode="wait" key="routes">
              <Routes location={location} key={location.pathname}>
                <Route path={route.onboarding} element={<OnboardingPage />} />

                <Route path={route.bots} element={<BotCategoriesPage />} />

                <Route path={route.botsList} element={<FuturesBotListPage />} />
                <Route path={route.botDetail} element={<FutureBotDetailsPage />} />

                <Route path={route.strategyList} element={<AutoStrategyListPage />} />
                <Route path={route.strategyDetail} element={<AutoStrategyDetailsPage />} />

                <Route path={route.choosePair} element={<TickerChoosePage />} />

                <Route path={route.creation} element={<CreationPage />} />

                <Route
                  path={route.notifications}
                  element={
                    <NotificationProvider
                      user={user}
                      notifications={notifications}
                      onChange={handleNotifications}
                    />
                  }
                />
                <Route path={route.profile} element={<ProfilePage />} />
                <Route path={route.profileAccount} element={<AccountCenterPage />} />
                <Route path={route.profileAppearance} element={<AppearancePage />} />
                <Route path={route.profileLanguage} element={<LanguagePage />} />
                <Route path={route.profileNotifications} element={<NotificationSettingsPage />} />
                <Route path={route.profileAbout} element={<AboutPage />} />

                <Route path={route.account.connect} element={<ConnectAccountPage />} />
                <Route path={route.account.settings} element={<AccountSettingsPage />} />

                <Route path={route.account.add} element={<ChooseMarketsPage />} />

                <Route path={route.leaderboard} element={<LeaderboardPage />} />
                <Route path={route.dashboard} element={<AssetsPage />} />
              </Routes>
            </AnimatePresence>
          </Box>

          {location.pathname !== "/" &&
            !NO_NAV_BAR_PAGES.some(
              (url) =>
                location.pathname.includes(url) &&
                !location.pathname.includes("/profile/notifications")
            ) && (
              <NavBar disabledCreateButton={!user.profile.accounts.length} key="navigation-bar" />
            )}
        </Box>
      )}
    </ColorModeProvider>
  );
}
